import { BrowserModule } from '@angular/platform-browser';
import { ErrorHandler, NgModule } from '@angular/core';
import { AdminRoutingModule } from './admin-routing.module';
import { AdminComponent } from './admin.component';
import { NotFoundComponent } from './shared/not-found/not-found.component';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { DashboardComponent } from './dashboard/dashboard.component';
import { CoreModule } from './core/core.module';
import { NgxSmartModalModule } from 'ngx-smart-modal';
import { NgbModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { NgProgressRouterModule } from 'ngx-progressbar/router';
import { NgProgressModule } from 'ngx-progressbar';
import { AccountModule } from './account/account.module';
import { SharedModule } from './shared/shared.module';
import { environment } from '../environments/environment';
import { init } from '@sentry/angular';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FinalizeComponent } from './finalize/finalize.component';
import { CustomErrorHandler } from './core/error-handler';
import { APP_BASE_HREF, DatePipe } from '@angular/common';
import { NgIconsModule, provideNgIconsConfig } from '@ng-icons/core';
import { heroArrowRightStartOnRectangle, heroCheck, heroCog6Tooth, heroDocumentCheck, heroInboxArrowDown, heroSquaresPlus, heroUserCircle, heroUser, heroPencilSquare, heroBars3, heroXMark, heroArrowUturnLeft, heroGift, heroCreditCard, heroArrowUpRight, heroLinkSlash, heroCheckBadge, heroPhone, heroCheckCircle, heroQuestionMarkCircle, heroChatBubbleBottomCenterText, heroChevronDown, heroArrowPath, heroPaperAirplane, heroArrowsRightLeft, heroPlusCircle, heroChatBubbleLeftRight, heroTag } from '@ng-icons/heroicons/outline';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { NavSidebarComponent } from './nav-sidebar/nav-sidebar.component';
import { UsageStatusComponent } from './nav-sidebar/usage-status/usage-status.component';
import { heroExclamationTriangleSolid } from '@ng-icons/heroicons/solid';
import { heroPauseCircleMini } from '@ng-icons/heroicons/mini';
import { SwitchAccountComponent } from './nav-sidebar/switch-account/switch-account.component';
import { WinbackModalComponent } from './dashboard/winback-modal/winback-modal.component';
import { BannedComponent } from './shared/banned/banned.component';
import { FeedbackComponent } from './nav-sidebar/feedback/feedback.component';

const isIgnoredError = (error: any) => {
  // Ignore null/undefined errors that would appear as <unknown> in Sentry
  if (error == null) {
    return true;
  }
  // Ignore errors with no meaningful message (e.g. Plyr media errors that surface as Error with undefined message)
  const errorMessage = error?.message || error?.name || (typeof error === 'string' ? error : '');
  if (!errorMessage || errorMessage === 'undefined') {
    return true;
  }
  const ignoredErrors = ['abort', 'network', 'unknown error', '0 unknown'];
  return ignoredErrors.some(ignored => errorMessage?.toLowerCase?.()?.includes?.(ignored));
};

if (environment.name !== 'local') {
  init({
    dsn: 'https://04f6fc85a282e1d0fbb584c9b774cea8@o504920.ingest.us.sentry.io/4507338457153536',
    environment: environment.name,
    integrations: [],
    tracesSampleRate: 0,
    release: environment.sentryRelease,
    beforeSend(event, hint) {
      const error = hint?.originalException;

      if (isIgnoredError(error)) {
        return null;
      }

      return event;
    },
  });

  CustomErrorHandler.isSentryEnabled = true;
}



const version = '0.0.1';
export function translateLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

@NgModule({
  declarations: [
    AdminComponent,
    NotFoundComponent,
    DashboardComponent,
    FinalizeComponent,
    NavSidebarComponent,
    UsageStatusComponent,
    SwitchAccountComponent,
    WinbackModalComponent,
    BannedComponent,
    FeedbackComponent
  ],
  bootstrap: [AdminComponent], imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AdminRoutingModule,
    CoreModule,
    NgProgressModule.withConfig({ color: '#9648bd', thick: true, spinner: false }),
    NgProgressRouterModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: (translateLoaderFactory),
        deps: [HttpClient]
      }
    }),
    NgxSmartModalModule.forRoot(),
    NgbModule,
    NgbTooltipModule,
    AccountModule,
    SharedModule,
    ToastModule,
    NgIconsModule.withIcons({
      heroUser, heroDocumentCheck, heroChatBubbleLeftRight, heroInboxArrowDown, heroCog6Tooth, heroSquaresPlus, heroUserCircle, heroArrowRightStartOnRectangle, heroCheck, heroPencilSquare, heroBars3, heroXMark,
      heroArrowUturnLeft, heroGift, heroCreditCard, heroArrowUpRight, heroExclamationTriangleSolid, heroLinkSlash, heroCheckBadge, heroPhone, heroCheckCircle,
      heroQuestionMarkCircle, heroChatBubbleBottomCenterText, heroChevronDown, heroArrowPath, heroPaperAirplane, heroArrowsRightLeft, heroPlusCircle, heroTag,
      heroPauseCircleMini
    }),
  ],
  providers: [
    {
      provide: ErrorHandler,
      useClass: CustomErrorHandler
    },
    provideHttpClient(withInterceptorsFromDi()),
    DatePipe,
    MessageService,
    provideNgIconsConfig({
      size: '20px',
      color: '#9487A0',
      strokeWidth: 2
    }),
    { provide: APP_BASE_HREF, useValue: '/admin' }
  ]
})
export class AdminModule {
}
